package fr.frux.feature.map.site

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.hybridMarkerTint
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapPlaque
import fr.frux.core.maps.PlaqueId
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_site_plaque_label
import fr.frux.feature.map.speciesFamilyColor
import fr.frux.feature.map.speciesGlyphKey
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.model.SiteBadge
import fr.frux.shared.domain.model.SiteHarvestSummary
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.badge
import fr.frux.shared.domain.model.dominantPlanting
import fr.frux.shared.domain.model.isLocked
import fr.frux.shared.domain.model.liveNextHarvest
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Instant

/**
 * Zoom a partir duquel les arbres rattaches a un lieu reapparaissent en epingles. En dessous, la
 * plaque du lieu les represente seule.
 */
const val SITE_DETAIL_ZOOM = 17f

/** Au-dela, le badge ne grandit plus : il doit tenir dans le coin de la plaque. */
private const val BADGE_CAP = 99

/** Badge d'un lieu sur demande d'acces : on ne promet pas un effectif qu'on ne peut pas aller voir. */
private const val ASK_BADGE = "?"

/**
 * Arbres a epingler : ceux d'un lieu affiche sont portes par sa plaque tant qu'on n'est pas au zoom
 * de detail. Sauf [keepId], l'arbre selectionne : sa bulle reste ouverte, son epingle aussi.
 */
fun List<Tree>.withoutAttachedTo(
    siteIds: Set<Long>,
    detailZoom: Boolean,
    keepId: Long? = null,
): List<Tree> =
    if (detailZoom || siteIds.isEmpty()) {
        this
    } else {
        filterNot { tree -> tree.id != keepId && tree.siteId?.let { it in siteIds } == true }
    }

/**
 * Le lieu est « bientot mur » quand un de ses arbres rattaches du lot fleurit et qu'aucun n'est deja
 * cueillable : la phase la plus avancee l'emporte, comme sur une epingle.
 */
fun Site.isFlowering(
    trees: List<Tree>,
    statusOf: (Tree) -> FruxStatus,
): Boolean {
    val statuses = trees.filter { it.siteId == id }.map(statusOf)
    return FruxStatus.Flowering in statuses && FruxStatus.Available !in statuses
}

/** « 8+ » pour un plancher, « 2 » pour un compte exact, plafonne a « 99+ ». */
fun SiteBadge.label(): String =
    when {
        count > BADGE_CAP -> "$BADGE_CAP+"
        atLeast -> "$count+"
        else -> "$count"
    }

private val MONTH_ABBREVIATIONS =
    listOf("janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc")

/** « 24 oct », « 3 nov » : jour et mois abrege sans point, dans le fuseau de l'appareil. */
fun SiteHarvestSummary.badgeLabel(zone: TimeZone = TimeZone.currentSystemDefault()): String {
    val date = Instant.fromEpochMilliseconds(scheduledAtMillis).toLocalDateTime(zone)
    return "${date.day} ${MONTH_ABBREVIATIONS[date.month.ordinal]}"
}

/**
 * Au zoom de detail les arbres du lieu reapparaissent a cote de la plaque : l'effectif est alors lisible
 * en les comptant, et son badge recouvrirait leurs pastilles. Le « ? » d'un lieu sur demande reste,
 * il dit une regle d'acces que les arbres ne montrent pas.
 */
internal fun countBadge(
    site: Site,
    detailZoom: Boolean = false,
): String? =
    when {
        site.accessRule == SiteAccessRule.ASK -> ASK_BADGE
        detailZoom -> null
        else -> site.badge()?.label()
    }

/**
 * Traduit les lieux en plaques de carte. `@Composable` parce que les teintes suivent le theme.
 *
 * Un lieu prive ferme (sans recolte a venir) ne montre ni espece ni effectif : seulement qu'il
 * existe et qu'il est ferme. Des qu'une recolte est a venir, le lieu reprend sa famille et porte
 * la date en pastille d'accent, quelle que soit sa regle d'acces.
 */
@Composable
fun List<Site>.toMapPlaques(
    selectedId: Long?,
    trees: List<Tree>,
    statusOf: (Tree) -> FruxStatus,
    nowMillis: Long,
    detailZoom: Boolean = false,
): List<MapPlaque> {
    val colors = MaterialTheme.fruxColors
    return map { site ->
        val position = Location(site.latitude, site.longitude)
        val label = stringResource(Res.string.map_site_plaque_label, site.name)
        val selected = site.id == selectedId
        val harvest = site.liveNextHarvest(nowMillis)
        if (site.isLocked(nowMillis)) {
            MapPlaque(
                id = PlaqueId(site.id),
                position = position,
                tint = colors.siteLocked,
                iconKey = FruxSpeciesGlyphs.KEY_CADENAS,
                selected = selected,
                label = label,
            )
        } else {
            val dominant = site.dominantPlanting()?.species
            val flowering = site.isFlowering(trees, statusOf)
            val family = speciesFamilyColor(dominant?.scientificName.orEmpty(), null, colors)
            MapPlaque(
                id = PlaqueId(site.id),
                position = position,
                tint = hybridMarkerTint(family, if (flowering) FruxStatus.Flowering else FruxStatus.Available, colors),
                iconKey = dominant?.let { speciesGlyphKey(it.scientificName, null) } ?: FruxSpeciesGlyphs.KEY_ARBRE,
                badge = harvest?.badgeLabel() ?: countBadge(site, detailZoom),
                badgeAccent = harvest != null,
                soon = flowering,
                selected = selected,
                label = label,
            )
        }
    }
}
